/**
 * CatchballParticipantConfig
 *
 * Setup panel that lets a cycle owner choose HOW Catchball participants are
 * determined for this cycle. Two modes:
 *
 *   Org chart (default) — assign specific people/groups at each working-tier
 *     org unit under cycle.orgScopeNodeId (the next level down from scope).
 *
 *   Custom participants — a virtual org chart: the user explicitly selects
 *     • who gets the ball (ball recipients, the "middle" tier)
 *     • who can participate by default (default participants, the "bottom" tier)
 *
 *   The ball can always add additional people ad-hoc during a live loop via
 *   the existing loop-level levelConfig mechanism; those additions are local
 *   to the loop and do not modify this config.
 *
 * Persisted to catchball_virtual_configs (migration 378).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Building2,
  CheckCircle2,
  Loader2,
  Network,
  Users,
  UserPlus,
  X,
  Info,
} from 'lucide-react';
import { useCatchballVirtualConfigStore } from '../../../stores/prioritization/catchballVirtualConfigStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useGroupsStore } from '../../../stores/prioritization/groupsStore';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { getSafeSupabaseClient } from '../../../lib/supabaseSync';
import { WorkingTierSetupPanel } from './WorkingTierSetupPanel';
import type { CatchballParticipantRef, CatchballSetupMode } from '../../../types/prioritization/catchballVirtualConfig';

// ── Interfaces ─────────────────────────────────────────────────────────────

interface WorkspaceUser {
  id: string;
  name: string;
  email: string;
}

interface WorkspaceGroup {
  id: string;
  name: string;
}

// ── Participant chip ─────────────────────────────────────────────────────────

function ParticipantChip({
  ref: p,
  onRemove,
}: {
  ref: CatchballParticipantRef;
  onRemove: () => void;
}) {
  const isGroup = p.type === 'group';
  return (
    <span
      className={`inline-flex items-center gap-1.5 pl-2 pr-1 py-0.5 rounded-full text-xs font-medium ${
        isGroup
          ? 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30'
          : 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/30'
      }`}
    >
      {isGroup ? (
        <Users className="h-3 w-3 shrink-0" />
      ) : (
        <div className="w-3 h-3 rounded-full bg-[var(--slate)] dark:bg-[var(--slate)] shrink-0" />
      )}
      <span className="truncate max-w-[120px]">{p.displayName}</span>
      <button
        type="button"
        onClick={onRemove}
        className="ml-0.5 rounded-full p-0.5 hover:bg-black/10 dark:hover:bg-[var(--bg-raised)]/10 transition-colors"
        aria-label={`Remove ${p.displayName}`}
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

// ── Participant picker section ─────────────────────────────────────────────

function ParticipantPickerSection({
  label,
  description,
  participants,
  users,
  groups,
  onAdd,
  onRemove,
}: {
  label: string;
  description: string;
  participants: CatchballParticipantRef[];
  users: WorkspaceUser[];
  groups: WorkspaceGroup[];
  onAdd: (ref: CatchballParticipantRef) => void;
  onRemove: (id: string) => void;
}) {
  const [addMode, setAddMode] = useState<'user' | 'group' | null>(null);
  const [search, setSearch] = useState('');

  const alreadyAddedIds = useMemo(() => new Set(participants.map((p) => p.id)), [participants]);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        !alreadyAddedIds.has(u.id) &&
        (!q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
    );
  }, [users, search, alreadyAddedIds]);

  const filteredGroups = useMemo(() => {
    const q = search.toLowerCase();
    return groups.filter(
      (g) => !alreadyAddedIds.has(g.id) && (!q || g.name.toLowerCase().includes(q))
    );
  }, [groups, search, alreadyAddedIds]);

  const handlePickUser = (u: WorkspaceUser) => {
    onAdd({ type: 'user', id: u.id, displayName: u.name || u.email });
    setSearch('');
    setAddMode(null);
  };

  const handlePickGroup = (g: WorkspaceGroup) => {
    onAdd({ type: 'group', id: g.id, displayName: g.name });
    setSearch('');
    setAddMode(null);
  };

  return (
    <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)]">
        <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">{label}</p>
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">{description}</p>
      </div>

      {/* Current participants */}
      <div className="px-5 py-3 min-h-[48px]">
        {participants.length === 0 ? (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-dim)] italic">
            No one added yet.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {participants.map((p) => (
              <ParticipantChip key={p.id} ref={p} onRemove={() => onRemove(p.id)} />
            ))}
          </div>
        )}
      </div>

      {/* Add picker */}
      <div className="px-5 pb-4">
        {addMode === null ? (
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setAddMode('user'); setSearch(''); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)]/50 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 transition-colors"
            >
              <UserPlus className="h-3 w-3" />
              Add user
            </button>
            {groups.length > 0 && (
              <button
                type="button"
                onClick={() => { setAddMode('group'); setSearch(''); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 border border-[var(--slate)] dark:border-[var(--slate)]/50 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 transition-colors"
              >
                <Users className="h-3 w-3" />
                Add group
              </button>
            )}
          </div>
        ) : addMode === 'user' ? (
          <div className="space-y-2 pt-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              autoFocus
              className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-400"
            />
            {(search || filteredUsers.length > 0) && (
              <div className="max-h-40 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {filteredUsers.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-[var(--text-ter)]">No users found</p>
                ) : filteredUsers.slice(0, 20).map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => handlePickUser(u)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <span className="font-medium truncate">{u.name || u.email}</span>
                    {u.name && <span className="text-xs text-[var(--text-ter)] truncate">{u.email}</span>}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => { setAddMode(null); setSearch(''); }}
              className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="space-y-2 pt-1">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search groups…"
              autoFocus
              className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-sm px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
            {(search || filteredGroups.length > 0) && (
              <div className="max-h-40 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
                {filteredGroups.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-[var(--text-ter)]">No groups found</p>
                ) : filteredGroups.slice(0, 20).map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => handlePickGroup(g)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <Users className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
                    <span className="truncate">{g.name}</span>
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => { setAddMode(null); setSearch(''); }}
              className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

interface CatchballParticipantConfigProps {
  cycleId: string;
  workspaceId: string;
  /**
   * Cycle org scope. When set, Org chart mode shows the working-tier people
   * picker (assign specific users/groups at each next-level org unit).
   */
  orgScopeNodeId?: string | null;
}

export function CatchballParticipantConfig({
  cycleId,
  workspaceId,
  orgScopeNodeId,
}: CatchballParticipantConfigProps) {
  const userId = useUserStore((s) => s.userId);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const resolvedWorkspaceId = workspaceId || currentWorkspaceId || '';

  const fetchForCycle = useCatchballVirtualConfigStore((s) => s.fetchForCycle);
  const save = useCatchballVirtualConfigStore((s) => s.save);
  const storedConfig = useCatchballVirtualConfigStore((s) => s.configs[cycleId] ?? null);
  const isLoading = useCatchballVirtualConfigStore((s) => s.loading[cycleId] ?? false);
  const storeError = useCatchballVirtualConfigStore((s) => s.errors[cycleId] ?? null);

  const orgUnitsMap = useEnterpriseIdentityStore((s) => s.orgUnits);
  const fetchOrgUnits = useEnterpriseIdentityStore((s) => s.fetchFromSupabase);
  const groupsFromStore = useGroupsStore((s) => s.groups);
  const fetchGroups = useGroupsStore((s) => s.fetchFromSupabase);
  const { users: systemUsers } = useSystemUsers({
    workspaceId: resolvedWorkspaceId || undefined,
  });

  // ── Local draft state ──────────────────────────────────────────────────────
  const [mode, setMode] = useState<CatchballSetupMode>(storedConfig?.setupMode ?? 'org_chart');
  const [ballRecipients, setBallRecipients] = useState<CatchballParticipantRef[]>(
    storedConfig?.ballRecipients ?? []
  );
  const [defaultParticipants, setDefaultParticipants] = useState<CatchballParticipantRef[]>(
    storedConfig?.defaultParticipants ?? []
  );
  const [saving, setSaving] = useState(false);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Workspace users / groups ───────────────────────────────────────────────
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [groups, setGroups] = useState<WorkspaceGroup[]>([]);
  const [loadingRoster, setLoadingRoster] = useState(false);

  const loadRoster = useCallback(async () => {
    const supabase = getSafeSupabaseClient();
    if (!supabase || !resolvedWorkspaceId) return;
    setLoadingRoster(true);
    try {
      const [{ data: rpcData }, { data: groupData }] = await Promise.all([
        supabase.rpc('get_workspace_participants', {
          p_workspace_id: resolvedWorkspaceId,
        }),
        supabase
          .from('groups')
          .select('id, name, display_name')
          .or(`workspace_id.eq.${resolvedWorkspaceId},workspace_id.is.null`)
          .order('name'),
      ]);
      setUsers(
        (rpcData ?? []).map(
          (u: Record<string, unknown>) =>
            ({
              id: u.id as string,
              name: ((u.name as string) || (u.email as string)) ?? '',
              email: (u.email as string) ?? '',
            }) satisfies WorkspaceUser
        )
      );
      setGroups(
        (groupData ?? []).map((g: { id: string; name?: string; display_name?: string }) => ({
          id: g.id,
          name: g.display_name || g.name || g.id,
        }))
      );
    } catch (e) {
      console.warn('[CatchballParticipantConfig] Failed to load roster:', e);
    } finally {
      setLoadingRoster(false);
    }
  }, [resolvedWorkspaceId]);

  // ── Initial fetch ─────────────────────────────────────────────────────────
  useEffect(() => {
    void fetchForCycle(cycleId);
    void loadRoster();
  }, [cycleId, fetchForCycle, loadRoster]);

  useEffect(() => {
    if (mode !== 'org_chart') return;
    if (orgUnitsMap.size === 0) void fetchOrgUnits();
    void fetchGroups();
  }, [mode, orgUnitsMap.size, fetchOrgUnits, fetchGroups]);

  // Sync draft when store loads
  useEffect(() => {
    if (storedConfig) {
      setMode(storedConfig.setupMode);
      setBallRecipients(storedConfig.ballRecipients);
      setDefaultParticipants(storedConfig.defaultParticipants);
    }
  }, [storedConfig]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAddBallRecipient = (ref: CatchballParticipantRef) => {
    setBallRecipients((prev) => [...prev.filter((p) => p.id !== ref.id), ref]);
  };
  const handleRemoveBallRecipient = (id: string) => {
    setBallRecipients((prev) => prev.filter((p) => p.id !== id));
  };
  const handleAddDefaultParticipant = (ref: CatchballParticipantRef) => {
    setDefaultParticipants((prev) => [...prev.filter((p) => p.id !== ref.id), ref]);
  };
  const handleRemoveDefaultParticipant = (id: string) => {
    setDefaultParticipants((prev) => prev.filter((p) => p.id !== id));
  };

  const handleSave = useCallback(async () => {
    if (!userId) return;
    setSaving(true);
    setSavedSuccess(false);
    const result = await save({
      cycleId,
      workspaceId: resolvedWorkspaceId,
      setupMode: mode,
      ballRecipients: mode === 'virtual' ? ballRecipients : [],
      defaultParticipants: mode === 'virtual' ? defaultParticipants : [],
      currentUserId: userId,
    });
    setSaving(false);
    if (result) {
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 3000);
    }
  }, [
    userId,
    save,
    cycleId,
    resolvedWorkspaceId,
    mode,
    ballRecipients,
    defaultParticipants,
  ]);

  // ── Dirty check ───────────────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    if (!storedConfig) return true;
    if (storedConfig.setupMode !== mode) return true;
    const refsEqual = (a: CatchballParticipantRef[], b: CatchballParticipantRef[]) =>
      a.length === b.length && a.every((r, i) => r.id === b[i]?.id && r.type === b[i]?.type);
    return (
      !refsEqual(storedConfig.ballRecipients, ballRecipients) ||
      !refsEqual(storedConfig.defaultParticipants, defaultParticipants)
    );
  }, [storedConfig, mode, ballRecipients, defaultParticipants]);

  // Auto-save so leaving Assemble the team doesn't drop unsaved picks.
  useEffect(() => {
    if (!isDirty || !userId || isLoading) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(() => {
      void handleSave();
    }, 600);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  }, [isDirty, userId, isLoading, handleSave]);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading && !storedConfig) {
    return (
      <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] py-6">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading configuration…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {storeError && !storedConfig && (
        <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] dark:border-[var(--mustard)]/50 dark:bg-[var(--mustard)]/30 px-4 py-3 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
          Could not load saved Catchball settings. You can still configure participants below.
        </div>
      )}

      {/* ── Mode selector ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Org chart option */}
        <button
          type="button"
          onClick={() => setMode('org_chart')}
          className={`relative  border-2 p-4 text-left transition-all ${
            mode === 'org_chart'
              ? 'border-[var(--green)] dark:border-[var(--green)] bg-[var(--green-dim)] dark:bg-[var(--green)]/20'
              : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)]'
          }`}
        >
          {mode === 'org_chart' && (
            <CheckCircle2 className="absolute top-3 right-3 h-4 w-4 text-[var(--green)] dark:text-[var(--text-ter)]" />
          )}
          <Building2
            className={`h-5 w-5 mb-2 ${
              mode === 'org_chart' ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)] dark:text-[var(--text-dim)]'
            }`}
          />
          <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Org chart</p>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
            Pick who gets the ball at each working-tier org unit under this cycle&apos;s scope.
          </p>
        </button>

        {/* Custom participants option */}
        <button
          type="button"
          onClick={() => setMode('virtual')}
          className={`relative  border-2 p-4 text-left transition-all ${
            mode === 'virtual'
              ? 'border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20'
              : 'border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] hover:border-[var(--rule)] dark:hover:border-[var(--rule-bright)]'
          }`}
        >
          {mode === 'virtual' && (
            <CheckCircle2 className="absolute top-3 right-3 h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          )}
          <Network
            className={`h-5 w-5 mb-2 ${
              mode === 'virtual' ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)] dark:text-[var(--text-dim)]'
            }`}
          />
          <p className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Custom participants</p>
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-1">
            Choose exactly who gets the ball and who can participate — no org chart required.
          </p>
        </button>
      </div>

      {/* ── Org chart: per-level people picker ───────────────────────────── */}
      {mode === 'org_chart' && (
        <div className="space-y-3 border border-[var(--green)]/80 dark:border-[var(--green)]/40 bg-[var(--green-dim)]/40 dark:bg-[var(--green)]/20 p-4">
          <div>
            <h4 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              Working tier — who receives the ball?
            </h4>
            <p className="mt-1 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              Expand each org unit and assign specific people or groups. Those assignments are who
              the first request goes to at that level.
            </p>
          </div>
          {!orgScopeNodeId ? (
            <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
              This cycle has no org scope yet. Set one in cycle configuration, or switch to Custom
              participants to name people directly.
            </p>
          ) : (
            <WorkingTierSetupPanel
              orgScopeNodeId={orgScopeNodeId}
              orgUnitsMap={orgUnitsMap}
              systemUsers={systemUsers}
              groups={groupsFromStore}
            />
          )}
        </div>
      )}

      {/* ── Virtual participant config ────────────────────────────────────── */}
      {mode === 'virtual' && (
        <div className="space-y-4">
          {/* Info banner */}
          <div className="flex items-start gap-2.5 border border-[var(--slate)] dark:border-[var(--slate)]/50 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/20 px-4 py-3 text-xs text-[var(--text-sec)]">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>
              <span className="font-medium">Custom participant mode.</span>{' '}
              You are the executive tier. Ball recipients receive the toss. Default participants
              contribute below them. Any ball holder can add people ad-hoc during a live loop
              without changing this configuration.
            </div>
          </div>

          {loadingRoster ? (
            <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] dark:text-[var(--text-dim)]">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Loading workspace members…
            </div>
          ) : (
            <>
              {/* Ball recipients */}
              <ParticipantPickerSection
                label="Ball recipients"
                description="Who receives the ball when you toss it. Equivalent to the 'middle management' tier."
                participants={ballRecipients}
                users={users}
                groups={groups}
                onAdd={handleAddBallRecipient}
                onRemove={handleRemoveBallRecipient}
              />

              {/* Default participants */}
              <ParticipantPickerSection
                label="Default participants"
                description="Who can participate by default — below the ball recipients. Equivalent to the 'front line' tier."
                participants={defaultParticipants}
                users={users}
                groups={groups}
                onAdd={handleAddDefaultParticipant}
                onRemove={handleRemoveDefaultParticipant}
              />
            </>
          )}
        </div>
      )}

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {storeError && (
        <p className="text-xs text-[var(--coral)] dark:text-[var(--coral)] bg-[var(--coral-dim)] dark:bg-[var(--coral)]/20 border border-[var(--coral)] dark:border-[var(--coral)]/40 px-3 py-2">
          {storeError}
        </p>
      )}

      {/* ── Save ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving || !isDirty}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[var(--green)] text-white hover:bg-[var(--green)] disabled:opacity-40 transition-colors"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {saving ? 'Saving…' : 'Save configuration'}
        </button>
        {savedSuccess && (
          <span className="inline-flex items-center gap-1.5 text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
            <CheckCircle2 className="h-4 w-4" />
            Saved
          </span>
        )}
        {!savedSuccess && isDirty && (
          <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Changes save automatically
          </span>
        )}
      </div>
    </div>
  );
}
